import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GetAgendas } from '../../graphql/agenda';
import { FindCompanyName } from '../../graphql/addCompany';
import { formatRut } from '../../utils/formatter';
import FeatherIcon from 'feather-icons-react';
import './ContraparteDropdown.scss';

const ContraparteDropdown = ({ 
  value, 
  onChange, 
  placeholder = "Ingrese Contraparte",
  className = "",
  disabled = false,
  allowCreate = true,
  /** Si se omite, la búsqueda por RUT en directorio sigue el mismo criterio que `allowCreate`. */
  allowRutLookup: allowRutLookupProp,
  selectedTextOverride,
  id,
  companyId,
  loadingText,
  /** Menú en document.body — usar dentro de modales con overflow para no recortar el listado */
  portalMenu = false,
  menuMaxHeight = 320,
  ...props 
}) => {
  const allowRutLookup = allowRutLookupProp !== undefined ? allowRutLookupProp : allowCreate;
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [showCreateMode, setShowCreateMode] = useState(false);
  const [createRut, setCreateRut] = useState('');
  const [createName, setCreateName] = useState('');
  const [nameFound, setNameFound] = useState('');
  const [rutSearchResult, setRutSearchResult] = useState(null); // Store RUT search result
  const [isSearchingRut, setIsSearchingRut] = useState(false); // Loading state for RUT search
  const [menuRect, setMenuRect] = useState(null);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const rutInputRef = useRef(null);
  const nameInputRef = useRef(null);

  // GraphQL hooks
  const [getAgendasInput, setGetAgendasInput] = useState({
    input: { companyId },
    pagination: { limit: 30 },
    search: '',
  });
  const { data, reexecuteQuery, fetching } = GetAgendas(getAgendasInput);
  const { executeMutation: findCompanyName } = FindCompanyName();

  // useEffect for agenda search
  useEffect(() => {
    if (showCreateMode) return; // Don't search in create mode
    
    const handler = setTimeout(() => {
      if (searchQuery.trim()) {
        setGetAgendasInput(prev => ({ ...prev, search: searchQuery }));
        reexecuteQuery({ requestPolicy: 'network-only' });
      } else {
        setGetAgendasInput(prev => ({ ...prev, search: '' }));
      }
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery, showCreateMode, reexecuteQuery]);

  // Check if search query looks like a RUT (8+ digits/K)
  const isRutFormat = (query) => {
    const cleaned = query.replace(/[^0-9Kk]/g, '');
    return cleaned.length >= 8;
  };

  // Search for company name when no results and query looks like RUT
  useEffect(() => {
    if (!allowRutLookup) {
      setRutSearchResult(null);
      setIsSearchingRut(false);
      return;
    }
    if (showCreateMode || !searchQuery.trim()) {
      setRutSearchResult(null);
      setIsSearchingRut(false);
      return;
    }

    // Only search if we have no agenda results and query looks like RUT
    const hasNoAgendaResults = !data?.paginatedSearchAgenda?.docs || data.paginatedSearchAgenda.docs.length === 0;
    // If agenda search just finished with no results and query looks like RUT, start RUT search immediately
    if (hasNoAgendaResults && isRutFormat(searchQuery) && !fetching) {
      const searchRutForName = async () => {
        const rutSearch = searchQuery.replace(/[^0-9Kk]/g, '');
        if (!rutSearch || rutSearch.length < 8) {
          setRutSearchResult(null);
          setIsSearchingRut(false);
          return;
        }

        // Start RUT search immediately (no gap in loading)
        setIsSearchingRut(true);
        try {
          const { data, error } = await findCompanyName({ rut: rutSearch });
          if (error || !data || data === "no") {
            setRutSearchResult(null);
            setIsSearchingRut(false);
            return;
          }

          // Create a result option from the found name
          if (data.findCompanyName) {
            setRutSearchResult({
              id: `rut-search-${rutSearch}`,
              key: `rut-search-${rutSearch}`,
              value: `rut-search-${rutSearch}`,
              text: `${formatRut(rutSearch)} :: ${data.findCompanyName}`,
              name: data.findCompanyName,
              rut: rutSearch,
              label: 'RUT encontrado',
              isRutSearch: true
            });
          }
        } catch (err) {
          setRutSearchResult(null);
        } finally {
          setIsSearchingRut(false);
        }
      };
      
      // Start RUT search immediately when agenda returns empty
      searchRutForName();
    } else if (!hasNoAgendaResults || !isRutFormat(searchQuery)) {
      // Only clear if we have results or it's not a RUT format
      setRutSearchResult(null);
      setIsSearchingRut(false);
    }
  }, [allowCreate, data, searchQuery, showCreateMode, fetching, findCompanyName, allowRutLookup]);

  // Process agenda data and include RUT search result
  const options = React.useMemo(() => {
    const agendaOptions = [];
    
    if (data?.paginatedSearchAgenda?.docs) {
      const agendas = data.paginatedSearchAgenda.docs;
      agendaOptions.push(...agendas.map((m) => {
        const rut = m?.worldWide === 'cl' ? formatRut(m?.rut) : m?.rut;
        const nameAndRut = `${rut} :: ${m?.name}`;
        const label =
          m?.isClient && m?.isProvider
            ? "Cliente - Proveedor"
            : m?.isClient
              ? "Cliente"
              : "Proveedor";
        return {
          id: m?.id,
          key: m?.id,
          value: m?.id,
          text: nameAndRut,
          name: m?.name,
          rut: m?.rut,
          label,
        };
      }));
    }

    // Add RUT search result if available and no agenda results
    if (allowRutLookup && rutSearchResult && agendaOptions.length === 0) {
      agendaOptions.push(rutSearchResult);
    }

    return agendaOptions;
  }, [allowRutLookup, data, rutSearchResult]);

  // Check for company name when RUT is entered in create mode
  useEffect(() => {
    if (showCreateMode && createRut && createRut.length >= 8) {
      getContraparteName();
    }
  }, [createRut, showCreateMode]);

  const getContraparteName = async () => {
    const rutSearch = createRut.replace(/[^0-9Kk]/g, '');
    if (!rutSearch || rutSearch.length < 8) return;

    const { data, error } = await findCompanyName({ rut: rutSearch });
    if (error || !data) return;
    if (data === "no") return;

    setNameFound(data.findCompanyName);
    if (data.findCompanyName && !createName) {
      setCreateName(data.findCompanyName);
    }
  };

  // Update selectedText when value changes (id agenda), opciones de búsqueda o etiqueta persistida (solo RUT / reabrir formulario)
  useEffect(() => {
    const hasValue = value != null && value !== '';
    const normalizedValue = hasValue ? String(value) : '';
    const selectedOption = normalizedValue
      ? options.find((opt) => String(opt.value) === normalizedValue)
      : null;

    if (selectedOption) {
      setSelectedText(selectedOption.text);
    } else if (selectedTextOverride) {
      setSelectedText(selectedTextOverride);
    } else if (!hasValue) {
      setSelectedText('');
    }
  }, [value, options, selectedTextOverride]);

  useLayoutEffect(() => {
    if (!portalMenu || !isOpen || !dropdownRef.current) {
      setMenuRect(null);
      return;
    }
    const rect = dropdownRef.current.getBoundingClientRect();
    setMenuRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [portalMenu, isOpen, searchQuery, options, fetching]);

  useEffect(() => {
    if (!portalMenu || !isOpen) return;
    const handleScrollOrResize = () => {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setMenuRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [portalMenu, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inMenu = portalMenu ? menuRef.current?.contains(event.target) : false;
      if (portalMenu) {
        if (!inTrigger && !inMenu) {
          setIsOpen(false);
          if (!showCreateMode) {
            setSearchQuery('');
          }
        }
      } else if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (!showCreateMode) {
          setSearchQuery('');
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showCreateMode, portalMenu]);

  // Handle option selection
  const handleSelect = (option) => {
    setSelectedText(option.text);
    setIsOpen(false);
    setSearchQuery('');
    setRutSearchResult(null);
    
    if (onChange) {
      // If it's a RUT search result, mark it as new so parent can handle it appropriately
      onChange({}, {
        value: option.isRutSearch ? 'add' : option.value,
        text: option.text,
        name: option.name,
        rut: option.rut,
        label: option.label,
        isNew: option.isRutSearch || false
      });
    }
  };

  // Handle create contraparte click
  const handleCreateClick = () => {
    if (!allowCreate) return;
    setShowCreateMode(true);
    setIsOpen(false);
    setSearchQuery('');
    // Focus on RUT input after a brief delay
    setTimeout(() => {
      rutInputRef.current?.focus();
    }, 100);
  };

  // Handle go back
  const handleGoBack = () => {
    setShowCreateMode(false);
    setCreateRut('');
    setCreateName('');
    setNameFound('');
    setIsOpen(false);
    setSearchQuery('');
    setRutSearchResult(null);
    
    // Clear parent form data if we were creating a new contraparte
    if (onChange && value === 'add') {
      onChange({}, {
        value: '',
        text: '',
        name: '',
        rut: '',
        label: '',
        isNew: false
      });
    }
  };

  // Handle create mode input changes
  const handleCreateRutChange = (e) => {
    const value = e.target.value.replace(/[^0-9Kk]/g, '');
    setCreateRut(value);
  };

  const handleCreateNameChange = (e) => {
    setCreateName(e.target.value);
  };

  // Store onChange in a ref to avoid dependency issues
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Helper function to notify parent
  const notifyParent = React.useCallback(() => {
    if (!allowCreate) return;
    if (showCreateMode && createRut && createRut.length >= 8 && createName && createName.trim()) {
      if (onChangeRef.current) {
        const rutFormatted = formatRut(createRut);
        onChangeRef.current({}, {
          value: 'add',
          text: `${rutFormatted} ${createName}`,
          name: createName,
          rut: createRut.replace(/[^0-9Kk]/g, ''),
          label: '(+)',
          isNew: true
        });
      }
    }
  }, [allowCreate, showCreateMode, createRut, createName]);

  // Handle create mode - notify parent when both fields are filled (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      notifyParent();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [notifyParent]);

  // Handle blur on name input to ensure parent is updated
  const handleNameBlur = () => {
    notifyParent();
  };

  // Handle input change for search
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (showCreateMode) {
        handleGoBack();
      } else {
        setIsOpen(false);
        setSearchQuery('');
        inputRef.current?.blur();
      }
    } else if (e.key === 'Enter' && options.length > 0 && !showCreateMode) {
      e.preventDefault();
      handleSelect(options[0]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen && !showCreateMode) {
        setIsOpen(true);
      }
    }
  };

  // Handle clearing selection
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedText('');
    setIsOpen(false);
    setSearchQuery('');
    setRutSearchResult(null);
    
    if (onChange) {
      onChange({}, {
        value: '',
        text: '',
        name: '',
        rut: '',
        label: '',
        isNew: false
      });
    }
  };

  const displayValue = loadingText && !selectedText ? loadingText : (isOpen && !showCreateMode ? searchQuery : selectedText);
  const showPlaceholder = !selectedText && !isOpen && !showCreateMode && !loadingText;

  const menuPanel = (
    <div className="dropdown-menu" style={portalMenu ? { maxHeight: menuMaxHeight } : undefined}>
      {options.length > 0 ? (
        <ul className="dropdown-options" role="listbox">
          {options.map((option) => (
            <li
              key={option.key}
              className={`dropdown-option ${String(value) === String(option.value) ? 'selected' : ''}`}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={String(value) === String(option.value)}
            >
              <span className="option-text">{option.text}</span>
              {option.label && (
                <span className="option-label">{option.label}</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="dropdown-no-results">
          {(fetching || isSearchingRut) ? (
            <div className="rut-search-loading">
              <div className="loading-spinner"></div>
              <div className="loading-message">
                {isSearchingRut ? 'Buscando razón social...' : 'Buscando...'}
              </div>
            </div>
          ) : searchQuery.trim() ? (
            <>
              <div className="no-results-message">
                {allowCreate
                  ? 'No se encontraron resultados. Ingresa el RUT completo para buscar su razón social'
                  : 'No se encontraron resultados.'}
              </div>
              {allowCreate && (
                <button
                  type="button"
                  className="create-contraparte-button"
                  onClick={handleCreateClick}
                >
                  <FeatherIcon icon="plus" size={16} />
                  Crear una contraparte
                </button>
              )}
            </>
          ) : (
            <div className="no-results-message">
              Ingresa el nombre o RUT para buscar
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Show create mode
  if (allowCreate && showCreateMode) {
    return (
      <div 
        ref={dropdownRef}
        className={`contraparte-dropdown ${className} ${disabled ? 'disabled' : ''}`}
        {...props}
      >
        <div className="create-mode-container">
          <div className="create-inputs-row">
            <div className="create-input-wrapper">
              <label className="create-label" htmlFor={`${id}-create-rut`}>RUT</label>
              <input
                ref={rutInputRef}
                id={`${id}-create-rut`}
                type="text"
                className="create-input"
                value={formatRut(createRut)}
                onChange={handleCreateRutChange}
                placeholder="Ingrese RUT"
                maxLength="12"
                disabled={disabled}
              />
            </div>
            <div className="create-input-wrapper">
              <label className="create-label" htmlFor={`${id}-create-name`}>RAZÓN SOCIAL O NOMBRE</label>
              <input
                ref={nameInputRef}
                id={`${id}-create-name`}
                type="text"
                className="create-input"
                value={createName}
                onChange={handleCreateNameChange}
                onBlur={handleNameBlur}
                placeholder="Ingrese Razón Social o Nombre"
                disabled={disabled}
              />
            </div>
            <button
              type="button"
              className="go-back-button"
              onClick={handleGoBack}
              disabled={disabled}
              title="Volver"
            >
              <FeatherIcon icon="arrow-left" size={18} />
            </button>
          </div>
          {nameFound && (
            <div className="name-found-hint">
              Razón social encontrada: {nameFound}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show dropdown mode
  return (
    <div 
      ref={dropdownRef}
      className={`contraparte-dropdown ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
      {...props}
    >
      <div className="dropdown-wrapper">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="dropdown-input"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={showPlaceholder ? placeholder : ''}
          disabled={disabled || fetching}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        />
        {selectedText && !isOpen && !disabled && (
          <button
            type="button"
            className="dropdown-clear-button"
            onClick={handleClear}
            title="Limpiar"
            tabIndex={0}
          >
            <FeatherIcon icon="x" size={16} />
          </button>
        )}
        <span className="dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
          </svg>
        </span>
        {fetching && (
          <span className="dropdown-loading">Cargando...</span>
        )}
      </div>
      {isOpen && !disabled && (
        portalMenu && menuRect && typeof document !== 'undefined'
          ? createPortal(
              <div
                ref={menuRef}
                className={`contraparte-dropdown contraparte-dropdown-portal-root ${className}`.trim()}
                style={{
                  position: 'fixed',
                  top: menuRect.top,
                  left: menuRect.left,
                  width: menuRect.width,
                  maxHeight: menuMaxHeight,
                  zIndex: 1000000,
                }}
              >
                {menuPanel}
              </div>,
              document.body
            )
          : portalMenu
            ? null
            : menuPanel
      )}
    </div>
  );
};

export default ContraparteDropdown;

